import React, { useState } from 'react';
import Button from '@mui/material/Button';

import Box from '@mui/material/Box';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Container from '@mui/material/Container';

import { styled } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { GENERAL_FONT } from '../../constants/theme';
import { DS } from '../../components/PatientForms/designTokens';
import { RootState } from '../../store/types/sharedTypes';
import {
  getAuthStatus,
  getAutostarttoken,
  AuthStatusResponse,
} from '../../lib/services/generalService';
import { performLogin } from '../../store/actions/general';
import type { AppDispatch } from '../../store';
import { reportError } from '../../lib/sentry';
import mhLogo from '../../../public/image/mh_logo.svg';
import teamDiscussing from '../../../public/image/team_discussing.png';

const LoginFormTextContainer = styled.div`
  width: 100%;
  display: flex;
  flex-align: left;
  flex-direction: column;
  text-align: center;
`;

function Copyright(props: TypographyProps) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {'Upphovsrätt © '}
      Migränhjälpen {new Date().getFullYear()}.
    </Typography>
  );
}

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loginError } = useSelector((state: RootState) => state.general);

  const [auth, setAuth] = useState<{
    status: string;
    inProgress: boolean;
    data: Partial<AuthStatusResponse> & { autoStartToken?: string };
  }>({
    status: 'not-started',
    inProgress: false,
    data: {},
  });

  const [error, setError] = useState<string | Error | null>(null);

  const { t } = useTranslation();

  const onSubmit = async () => {
    setAuth({
      status: 'not-started',
      inProgress: false,
      data: {},
    });
    setError(null);

    setAuth((ctx) => ({
      ...ctx,
      status: 'auth-started',
      inProgress: true,
    }));

    try {
      const response = await getAutostarttoken();

      if (response.status !== 200 || !response.data)
        throw new Error('Auth process could not be started');

      const { transactionId, autoStartToken } = response.data;

      let authData: Partial<AuthStatusResponse> & { autoStartToken?: string } =
        { autoStartToken };

      const authStatus = await getAuthStatus(
        transactionId,
        (data: AuthStatusResponse) => {
          authData = { ...authData, ...data };
          setAuth((ctx) => ({ ...ctx, data: authData, status: data.status }));
        }
      );

      if (authStatus.status === 'complete' && authStatus.token) {
        setAuth((ctx) => ({
          ...ctx,
          status: 'authenticated',
          inProgress: false,
        }));
        dispatch(performLogin(authStatus.token));
      } else if (authStatus.status === 'rejected') {
        setAuth((ctx) => ({ ...ctx, status: 'rejected', inProgress: false }));
      } else {
        setAuth((ctx) => ({ ...ctx, status: 'unknown', inProgress: false }));
      }
    } catch (err) {
      reportError(err, { tags: { area: 'bankid_login' } });
      setError(err instanceof Error ? err : new Error(String(err)));
      setAuth((ctx) => ({
        ...ctx,
        status: 'not-started',
        inProgress: false,
      }));
    }
  };

  const launchDesktopApp = () => {
    if (auth.data.autoStartToken) {
      const desktopAppUrl = `bankid:///?autostarttoken=${auth.data.autoStartToken}&redirect=null`;
      window.location.href = desktopAppUrl;
    } else {
      setError('No auto start token available.');
    }
  };

  const hasError = loginError || error;

  return (
    <Grid
      container
      style={{ height: '100vh', backgroundColor: DS.dawnSurface }}
    >
      <Grid item xs={5} style={{ display: 'flex' }}>
        <Container component="main" maxWidth="xs" style={{ margin: 'auto' }}>
          <Box
            component="img"
            src={mhLogo}
            alt="Migränhjälpen"
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: 200,
              height: 'auto',
              marginBottom: '30px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          />

          <Box
            sx={{
              marginTop: '50px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: DS.surfaceRaised,
              padding: '24px',
              borderRadius: '5px',
              minHeight: '20vh',
            }}
          >
            <LoginFormTextContainer>
              <Typography
                sx={{
                  fontFamily: GENERAL_FONT,
                  color: DS.deepTeal,
                  alignItems: 'left',
                  fontSize: '25px',
                }}
              >
                {t('welcome')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: GENERAL_FONT,
                  fontWeight: '600',
                  paddingTop: '1vh',
                  paddingBottom: '1vh',
                  color: DS.deepTeal,
                  fontSize: '25px',
                  alignItems: 'left',
                }}
              >
                {t('login')}
              </Typography>
            </LoginFormTextContainer>
            {hasError && (
              <Box
                display="flex"
                flexDirection="column"
                style={{
                  rowGap: 9,
                  textAlign: 'center',
                  marginBottom: '20px',
                }}
              >
                <Typography textAlign="center">{t('bankIdError')}</Typography>
              </Box>
            )}
            {auth.inProgress && !hasError && auth.data.qrCode && (
              <QRCodeSVG
                value={auth.data.qrCode}
                size={250}
                style={{ marginBottom: '20px' }}
              />
            )}
            <Typography
              style={{ fontFamily: GENERAL_FONT, textAlign: 'center' }}
            >
              {auth.inProgress && !hasError && t('scanBankIdQR')}
            </Typography>
            {(!auth.inProgress || hasError) && (
              <Button
                onClick={onSubmit}
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  fontFamily: GENERAL_FONT,
                  maxWidth: '180px',
                  backgroundColor: DS.deepTeal,
                  color: DS.surfaceCanvas,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: DS.deepTeal,
                    filter: 'brightness(0.96)',
                  },
                }}
              >
                {t('loginWithBankId')}
              </Button>
            )}
            {auth.inProgress && auth.data.autoStartToken && !hasError && (
              <Button
                onClick={launchDesktopApp}
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  mb: 2,
                  fontFamily: GENERAL_FONT,
                  maxWidth: '250px',
                  backgroundColor: DS.deepTeal,
                  color: DS.surfaceCanvas,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: DS.deepTeal,
                    filter: 'brightness(0.96)',
                  },
                }}
              >
                {t('loginWithDesktopApp')}
              </Button>
            )}
          </Box>
          <Copyright sx={{ mt: 8 }} />
        </Container>
      </Grid>
      <Grid item xs={7} style={{ display: 'flex' }}>
        <Box style={{ margin: 'auto' }} component="img" src={teamDiscussing} />
      </Grid>
    </Grid>
  );
};

export default Login;
